import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, X } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { EASE } from "./motion";

const API = process.env.REACT_APP_BACKEND_URL;

const Tile = ({ m, i, onOpen }) => (
  <motion.button type="button" onClick={() => onOpen(m)} data-testid={`work-tile-${m.id}`}
    variants={{ hidden: { clipPath: "inset(100% 0 0 0)", y: 20 }, show: { clipPath: "inset(0% 0 0 0)", y: 0 } }} transition={{ duration: 0.9, ease: EASE }}
    whileHover={{ y: -6 }} whileTap={{ scale: 0.97 }}
    className={`group relative block w-full overflow-hidden bg-stone-200 text-left ${i % 5 === 0 ? "sm:col-span-2 aspect-[16/10]" : "aspect-[4/5]"}`}>
    {m.kind === "video" ? (
      <video src={`${API}${m.url}#t=0.1`} muted playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover" />
    ) : (
      <img src={`${API}${m.url}`} alt={m.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
    {m.kind === "video" && <span className="absolute inset-0 m-auto w-12 h-12 rounded-full border border-white/70 bg-white/10 backdrop-blur flex items-center justify-center"><Play size={16} className="fill-white text-white ml-0.5" /></span>}
    <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">{m.category}</p>
      <h3 className="font-serif text-xl mt-1 leading-tight">{m.title}</h3>
    </div>
  </motion.button>
);

export const WorkGallery = ({ limit = 60, compact = false }) => {
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("All");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/media/categories`).then((r) => r.json()).then(setCats).catch(() => {});
  }, []);
  useEffect(() => {
    setItems(null);
    fetch(`${API}/api/media?limit=${limit}${cat !== "All" ? `&category=${encodeURIComponent(cat)}` : ""}`).then((r) => r.json()).then(setItems).catch(() => setItems([]));
  }, [cat, limit]);

  return (
    <section id="work" className={`max-w-7xl mx-auto px-6 lg:px-10 ${compact ? "py-20" : "py-24 lg:py-36"}`} data-testid="work-gallery">
      <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
        <div>
          <Eyebrow className="mb-6">From the studio</Eyebrow>
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-stone-900">{compact ? "Latest work" : "Designs & site photos"}</h2>
          <p className="mt-4 max-w-lg text-stone-600 font-light leading-relaxed">Renders, drawings, site progress and short videos — uploaded by our team as projects move forward.</p>
        </div>
        {!compact && cats.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="work-filter">
            {["All", ...cats].map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`work-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`relative px-4 py-2.5 font-mono text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${cat === c ? "text-[#FBFBF9] border-stone-900" : "border-stone-300 text-stone-600 hover:border-stone-900"}`}>
                {cat === c && <motion.span layoutId="work-pill" className="absolute inset-0 bg-stone-900" transition={{ type: "spring", stiffness: 400, damping: 34 }} />}
                <span className="relative">{c}</span>
              </button>
            ))}
          </div>
        )}
      </Reveal>

      {items === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[0, 1, 2].map((i) => <div key={i} className="aspect-[4/5] bg-stone-200 animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <p className="border border-dashed border-stone-300 p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-stone-500" data-testid="work-empty">New work is being uploaded — check back soon</p>
      ) : (
        <motion.div key={cat} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" initial="hidden" whileInView="show" viewport={{ once: true, amount: "some" }} transition={{ staggerChildren: 0.08 }} data-testid="work-grid">
          {items.map((m, i) => <Tile key={m.id} m={m} i={i} onOpen={setOpen} />)}
        </motion.div>
      )}

      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-[80] bg-[#161614]/95 backdrop-blur flex items-center justify-center p-4 lg:p-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(null)} data-testid="work-lightbox">
            <button className="absolute top-5 right-5 w-11 h-11 rounded-full border border-stone-600 text-[#FBFBF9] flex items-center justify-center" aria-label="Close" data-testid="work-lightbox-close"><X size={18} /></button>
            <motion.figure initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }} transition={{ duration: 0.4, ease: EASE }} className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              {open.kind === "video" ? (
                <video src={`${API}${open.url}`} controls autoPlay playsInline className="w-full max-h-[75vh] bg-black" data-testid="work-lightbox-video" />
              ) : (
                <img src={`${API}${open.url}`} alt={open.title} className="w-full max-h-[75vh] object-contain" data-testid="work-lightbox-image" />
              )}
              <figcaption className="mt-4 flex flex-wrap items-end justify-between gap-3 text-[#FBFBF9]">
                <div><p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C29B38]">{open.category}</p><p className="font-serif text-2xl mt-1">{open.title}</p>{open.caption && <p className="text-sm text-stone-400 mt-1 max-w-xl">{open.caption}</p>}</div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{open.uploaded_by} · {new Date(open.created_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}</p>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
